import { useEffect, useState } from "react";
import { Alert, Button, Card, Container, Form, ListGroup, Spinner } from "react-bootstrap";
import { isAxiosError } from "axios";
import { getMyRoles } from "@/api/adminApi";
import { searchUsers } from "@/services/resourcesApi";
import { getCompliancePersonResources, getCompliancePersonSummary, getComplianceSnapshots } from "@/services/resourceComplianceService";
import type { CompliancePersonSummary, ComplianceSnapshot } from "@/types/resourceCompliance";
import { canUseResourceHealthDemo, demoHealthLoadError, normalizeDemoPeople, orderHealthSnapshots, selectDemoSnapshots, snapshotLabel } from "./resourceHealthDemo";
import type { DemoPerson, SnapshotResult } from "./resourceHealthDemo";
import ResourceHealthDemoResults from "./ResourceHealthDemoResults";
import EmailSnapshotButton from "./EmailSnapshotButton";

const ResourceHealthDemoPage = () => {
  const [ready, setReady] = useState(false);
  const [allowed, setAllowed] = useState(false);
  const [snapshots, setSnapshots] = useState<ComplianceSnapshot[]>([]);
  const [view, setView] = useState("compare");
  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState<DemoPerson[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchDone, setSearchDone] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selected, setSelected] = useState<DemoPerson | null>(null);
  const [results, setResults] = useState<SnapshotResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const roles = await getMyRoles();
        const canView = canUseResourceHealthDemo(roles);
        if (!cancelled) setAllowed(canView);
        if (!canView) return;
        const rows = orderHealthSnapshots(await getComplianceSnapshots());
        if (!cancelled) setSnapshots(rows);
      } catch {
        if (!cancelled) setError("Unable to load demo access or snapshots. Reload the page to retry.");
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!allowed || selected || search.trim().length < 2) return;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setSearching(true);
      setSearchError(null);
      try {
        const people = normalizeDemoPeople(await searchUsers(search.trim()));
        if (!cancelled) { setSuggestions(people); setSearchDone(true); }
      } catch {
        if (!cancelled) setSearchError("Person search failed. Edit your search to retry.");
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [allowed, search, selected]);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    setLoading(true);
    setResults([]);
    setError(null);
    const chosen = selectDemoSnapshots(snapshots, view);
    (async () => {
      try {
        const data = await Promise.all(chosen.map(async (snapshot): Promise<SnapshotResult> => {
          let person: CompliancePersonSummary;
          try {
            person = await getCompliancePersonSummary(snapshot.snapshotId, selected.email);
          } catch (failure) {
            if (isAxiosError(failure) && failure.response?.status === 404) {
              return { snapshot, person: null, resources: [] };
            }
            throw failure;
          }
          const resources = await getCompliancePersonResources(snapshot.snapshotId, selected.email);
          return { snapshot, person, resources };
        }));
        if (!cancelled) setResults(data);
      } catch (failure) {
        if (!cancelled) setError(demoHealthLoadError(isAxiosError(failure) ? failure.response?.status : undefined));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selected, view, snapshots]);

  const reset = () => {
    setSelected(null);
    setSearch("");
    setSuggestions([]);
    setResults([]);
    setError(null);
    setSearchError(null);
    setSearching(false);
    setSearchDone(false);
    setLoading(false);
    setView("compare");
  };

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
        <div>
          <h2>Discover your Resource Health</h2>
          <p className="text-muted mb-0">Find yourself to explore your Microsoft 365 resource health, then and now.</p>
        </div>
        {allowed && selected && <div className="d-flex align-items-start gap-2 flex-wrap">
          <Button variant="outline-secondary" onClick={reset}>Start over</Button>
          {!loading && results.length > 0 && <EmailSnapshotButton
            key={`${selected.email}:${results.map((result) => result.snapshot.snapshotId).join(",")}`}
            personEmail={selected.email} results={results} />}
        </div>}
      </div>
      {!ready && <Spinner animation="border" role="status"><span className="visually-hidden">Loading demo</span></Spinner>}
      {error && <Alert variant="danger" role="alert">{error}</Alert>}
      {ready && !allowed && !error && <Alert variant="warning">This hosted demo requires the ResourceHealthDemo role (or SuperAdmin / ResourceComplianceAdmin access).</Alert>}
      {ready && allowed && <>
        {!selected && <Card body className="mb-4">
          <Form.Label htmlFor="health-demo-search" className="fw-semibold">Search your name or email</Form.Label>
          <Form.Control
            id="health-demo-search" autoFocus type="search" size="lg"
            placeholder="Start typing your name or email..." autoComplete="off"
            disabled={snapshots.length === 0}
            value={search} aria-describedby="health-demo-search-help"
            onChange={(event) => {
              setSelected(null); setResults([]); setLoading(false); setError(null); setSuggestions([]);
              setSearchError(null); setSearching(false); setSearchDone(false); setSearch(event.target.value);
            }}
          />
          <div id="health-demo-search-help" className="small text-muted mt-2">
            Enter at least two characters, then select your name. This is a shared, administrator-hosted demo; it can display any selected person.
          </div>
          <div aria-live="polite">
            {searching && <div className="mt-2"><Spinner animation="border" size="sm" /> Searching people...</div>}
            {searchError && <Alert variant="danger" className="mt-2 mb-0">{searchError}</Alert>}
            {!selected && searchDone && suggestions.length === 0 && <p className="mt-2 mb-0">No matching people. Try another name or email.</p>}
          </div>
          {!selected && <ListGroup className="mt-2">
            {suggestions.map((person) => <ListGroup.Item action key={person.email} onClick={() => {
              setSelected(person); setSearch(person.displayName); setSuggestions([]); setSearching(false);
            }}>
              <strong>{person.displayName}</strong><span className="text-muted ms-2">{person.email}</span>
            </ListGroup.Item>)}
          </ListGroup>}
          {snapshots.length === 0 && <Alert variant="info" className="mt-3 mb-0">No Resource Health snapshots are available.</Alert>}
        </Card>}
        {selected && snapshots.length > 0 && <>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
            <div><h3 className="mb-1">{selected.displayName}</h3><div className="text-muted">{selected.email}</div></div>
            <div>
              <Form.Label htmlFor="health-demo-view">Snapshot view</Form.Label>
              <Form.Select id="health-demo-view" value={view} onChange={(event) => {
                setResults([]); setLoading(true); setView(event.target.value);
              }}>
                <option value="compare">{snapshots.length > 1 ? "Compare latest two snapshots" : "Latest snapshot"}</option>
                {snapshots.map((snapshot) => <option key={snapshot.snapshotId} value={snapshot.snapshotId}>{snapshotLabel(snapshot)}</option>)}
              </Form.Select>
            </div>
          </div>
          {loading && <div role="status"><Spinner animation="border" /> Loading snapshot health...</div>}
          {!loading && results.length > 0 && <ResourceHealthDemoResults results={results} comparing={view === "compare"} />}
        </>}
      </>}
    </Container>
  );
};

export default ResourceHealthDemoPage;
